import { prepareC1NavigatorDeletion } from "./NavigationTree2/c1NavigatorDeletion";
import BaseModel from "../models/BaseModel";
import BaseModelMap from "../models/BaseModelMap";
import BaseObject from "../models/BaseObject";
import type Connection from "../models/Connection";
import { deleteVirtualConnection } from "../connectionAnalyser/VirtualConnections";
import ObjectMapping from "../models/ObjectMapping";
import ObjectRegistry from "../models/ObjectRegistry";
import ZipStorageHandler from "../persistence/ZipStorageHandler";
import Device from "../models/Device";

import { showToast } from "./Toasts";
import { showDeletionPreventedToast } from "./showDeletionPreventedToast";
import DeviceReference from "../models/DeviceReference";
import Logger from "../Logger";
import ConfirmDialog from "../modelDialogs/ConfirmDialog";
import ButtonChannel from "../models/ButtonChannel";
import { EventKey } from "../eventLogger/EventKeys";

const logger = new Logger();

/** Preserves the established special handling for deleting a single button channel. */
function deleteModelObject(object: BaseModel): void {
  if (object.className === "ButtonChannel") (object as ButtonChannel).deleteOnlyThisChannel();
  else object.delete();
}

export default class MenubarActions {
  public get className(): string {
    return "MenubarActions";
  }

  async onDelete(objDelete?: BaseModel, withCopy?: boolean) {
    const virtual = state.connectionAnalyser.selectedVirtualConnection;
    if (!objDelete && !withCopy && virtual) {
      state.undoManager.runAsSingleStep("Verbindung löschen", () => {
        deleteVirtualConnection(virtual);
        state.connectionAnalyser.selectedVirtualConnectionId.value = undefined;
        state.selectionManager.clearSelectionPreservingAnalysis();
      });
      return;
    }
    const selectedObject = objDelete ?? state.selectionManager.selectedObject;
    if (!selectedObject || selectedObject.isDeleted) return;
    if (selectedObject.className === "C1_Wall") {
      if (state.c1.requestWallDeletion(selectedObject) === false)
        showDeletionPreventedToast(selectedObject, state.c1.editMode.value);
      return;
    }
    if (state.c1.isDeletionProtectedInDetailMode(selectedObject)) {
      showDeletionPreventedToast(selectedObject, state.c1.editMode.value);
      return;
    }
    const focusAfterDeletion = prepareC1NavigatorDeletion(selectedObject);
    // Load C1 rules only when needed; static imports would pull its model graph into app startup.
    const c1Deletion = selectedObject.c1
      ? await import("../../ConceptOne/src/model/C1_interactiveDeletion") : undefined;
    if (selectedObject.isDeleted) return;

    function executeDelete() {
      if (selectedObject && state.c1.isDeletionProtectedInDetailMode(selectedObject)) {
        showDeletionPreventedToast(selectedObject, state.c1.editMode.value);
        return;
      }
      if (selectedObject && !selectedObject.isDeleted) {
        const objectToDelete = selectedObject;
        const objectDescription = [objectToDelete.fullTag, objectToDelete.name].filter(Boolean).join(" ");
        state.undoManager.runAsSingleStep(`${objectDescription || objectToDelete.className} löschen`, () => {
          let nextSelection: BaseModel | undefined;
          if (c1Deletion) nextSelection = c1Deletion.deleteC1Object(objectToDelete, state.c1, deleteModelObject);
          else deleteModelObject(objectToDelete);
          state.eventLogger.addEvent(EventKey.ObjectDeleted, {
            objectType: objectToDelete.className,
            obj: objectToDelete.id + "_" + objectToDelete.tag + "_" + objectToDelete.name,
          });
          if (objectToDelete.className === "Connection" || objectToDelete.className === "VirtualConnection") state.selectionManager.clearSelectionPreservingAnalysis();
          else state.selectionManager.clear();
          if (nextSelection) state.selectionManager.setSelectedObjectWithoutNavigatorUpdate(nextSelection);
        });
        focusAfterDeletion?.();
      }
    }

    if (selectedObject) {
      const canBeDeletedInteractive = selectedObject.canBeDeletedInteractive();

      if (!canBeDeletedInteractive) {
        const text = selectedObject.getDeleteConfirmText();
        if (text) {
          state.dialogManager.openDialog(
            <ConfirmDialog
              text={text}
              title={
                withCopy
                  ? state.translate("menubarActions.headerCut", "Aussschneiden")
                  : state.translate("menubarActions.headerDelete", "Löschen")
              }
              onOk={() => {
                executeDelete();
                if (withCopy) {
                  this.onCopy(objDelete);
                }
                return true;
              }}
              okCaption={
                withCopy
                  ? state.translate("menubarActions.cutAnyway", "trotzdem ausschneiden")
                  : state.translate("menubarActions.deleteAnyway", "trotzdem löschen")
              }
              onClose={() => {
                return true;
              }}
            />,
          );
        } else {
          showDeletionPreventedToast(selectedObject, state.c1.editMode.value);
        }
      } else {
        executeDelete();
        if (withCopy) {
          this.onCopy(objDelete);
        }
      }
    }
  }

  async onCopy(objCopy?: BaseModel) {
    state.objectRegistry.clearIsSaved();

    var selectedObject: any = state.selectionManager.selectedObject;
    if (objCopy) {
      selectedObject = objCopy;
    }
    if (selectedObject) {
      // if its an reference copy the referenced obj.
      if (selectedObject.className == "DeviceReference") {
        const deviceRef = selectedObject as DeviceReference;
        if (deviceRef.referencedObject) {
          selectedObject = deviceRef.referencedObject;
        }
      }
      const zipStorageHandler = new ZipStorageHandler();
      const items = new BaseModelMap<BaseModel>(BaseModel, undefined, undefined);
      items.id = "clipboard"; // no itx
      var itemsExt = new BaseModelMap<BaseModel>(BaseModel, undefined, undefined);
      itemsExt.id = "extended"; // no itx
      // at the moment only one item
      items.addWithoutUpdate(selectedObject);

      state.clipboardItems = items; // copping the obj with all is hirarchish children

      items.forEach((item) => {
        extendCopyItems(items, itemsExt, item);
      });

      // Lösche Eintrage gleich einem Eintag in "items" ist, oder ein Kind eines  Eintrags in "items" sind

      var itemsExtArray = itemsExt.asArray();
      var itemsArray = items.asArray();
      // Lösche Eintrage die ein Kind eines anderen Eintrags in "itemsExt" sind

      // itemsExt.items.clear();

      // itemsExtArray.forEach((itemExt) => {
      //   if (itemExt.parent) {
      //     if (
      //       !itemsExtArray.includes(itemExt.parent as BaseModel) &&
      //       itemsArray.includes(itemExt.parent as BaseModel)
      //     ) {
      //       itemsExt.add(itemExt);
      //     }
      //   }
      // });

      itemsExtArray.forEach((item) => {
        console.log("ExtArr", item.tag, item.name);
      });

      await zipStorageHandler.saveItemsForClipboard(items);
      await zipStorageHandler.saveItemsForClipboard(itemsExt);

      items.forEach((item) => {
        if (itemsExt.items.has(item.id)) {
          logger.error(`item inside itemsExt`);
        }
      });

      const blob = await zipStorageHandler.getBlob();
      let blobUrl = await convertBlobToBase64Async(blob);
      if (!blobUrl) {
        return;
      }

      const item0 = items.asArray()[0];

      if (item0) {
        let classNameItem = item0.className;
        state.clipboard.setDescription(`${item0.fullTag} ${item0.fullname}`);
        state.clipboard.setData(blobUrl, classNameItem);
      } else {
        state.clipboard.setDescription("");
        state.clipboard.setData("", "");
      }
    }
  }
  async onPaste(objOnPaste?: BaseModel): Promise<Array<BaseModel> | undefined> {
    const res = await this.onPaste2(objOnPaste);
    return res?.newItems;
  }

  async onPaste2(
    objOnPaste?: BaseModel,
    withoutIdMapping?: boolean,
  ): Promise<
    | {
        newItems: Array<BaseModel>;
        newReferencedItems: Array<BaseModel>;
      }
    | undefined
  > {
    try {
      const txt = state.clipboard.getData();
      if (!txt) {
        return undefined;
      }
      const blob = await convertBase64toBlobAsync(txt);

      logger.log(`second ClipbordItem`, txt);

      //const firstItem = clipboardItems[0];

      if (true) {
        if (blob) {
          // const blob = await clipboardItems[0].getType(type);

          const zipStorageHandler = new ZipStorageHandler();
          await zipStorageHandler.setBlob(blob);
          state.targetObjectRegistry = state.objectRegistry;

          state.objectRegistry = new ObjectRegistry();
          const items = new BaseModelMap<BaseModel>(BaseModel, undefined, undefined);
          items.id = "clipboard"; // no itx
          const itemsExt = new BaseModelMap<BaseModel>(BaseModel, undefined, undefined);
          itemsExt.id = "extended"; // no itx
          //
          // load all objects from clipboard to global project/state/objectRegistry
          //
          await items.loadFromEstadaStorage(
            undefined,
            undefined,
            async (id: string | undefined, parentId: string | undefined): Promise<any> => {
              return await zipStorageHandler.loadData(id, parentId);
            },
          );
          await itemsExt.loadFromEstadaStorage(
            undefined,
            undefined,
            async (id: string | undefined, parentId: string | undefined): Promise<any> => {
              return await zipStorageHandler.loadData(id, parentId);
            },
          );
          logger.log(`is done paste`);

          state.objectRegistry.allObjects.forEach((connection) => {
            if (connection.className == "Connection") {
              (connection as Connection).addToConnectableObjectsWithoutUpdate();
            }
          });

          state.clipboardItems = items;

          const allObjects = Array<BaseObject>();
          state.objectRegistry.allObjects.forEach((element) => {
            allObjects.push(element);
          });

          if (!withoutIdMapping) {
            //
            // map all obejcts loaded from clipboard to new IDs
            //
            state.objectMapping = new ObjectMapping();
            // put all items in the mapping table
            allObjects.forEach((obj) => {
              const mappedId = state.objectMapping.getMappedId(obj.id);
            });

            // call map afterCopy for all items

            allObjects.forEach((obj) => {
              obj.mapAfterCopy();
            });
          }

          //
          // attach/add connections to ConnectableObject.connections
          //
          // connections may never be part of the primary copy collection
          // so we just need to scan "itemsExt"

          allObjects.forEach((obj) => {
            if (obj.className == "Connection") {
              const connection = obj as Connection;
              connection.addToConnectableObjectsWithoutUpdate(); // wiederherstellung des netzwerkes (z.b. consumer)
            }
          });

          //
          // restore project to original state
          state.objectMapping = new ObjectMapping();

          state.objectRegistry = state.targetObjectRegistry;
          state.targetObjectRegistry = new ObjectRegistry();

          allObjects.forEach((obj) => {
            state.objectRegistry.registerObject(obj);
          });

          // get the selected Obj

          //
          // all items, that are pasted or created during the paste process
          //
          var allNewItems = new Array<BaseModel>();
          //////////////////////////////////////////////////////////////////

          const itemsArray = items.asArray();
          var selectedObj = state.selectionManager.selectedObject;
          if (objOnPaste) {
            selectedObj = objOnPaste;
          }

          if (selectedObj) {
            if (selectedObj.className == "BaseModelMap")
              if ((selectedObj as BaseModelMap<BaseModel>).propertyId == "sg") {
                //     // copy to sceneGroup has to be implemented
              }
          }

          if (selectedObj) {
            for (let item of itemsArray) {
              allNewItems = allNewItems.concat(
                await selectedObj.pasteItem({
                  item: item,
                  allItems: itemsArray,
                }),
              );
            }

            if (itemsArray.length > 0) {
              state.selectionManager.selectedObject = itemsArray[0];
              state.selectionManager.selectedObject.etsId = ""; // New Object should not copy etsId of clipboard object
              (document.activeElement as HTMLElement)?.blur();
            }
          }

          const res = new Array<BaseModel>();
          //
          //  delete all unused items in "items" and "itemsExt"
          //
          items.forEach((item) => {
            var used = false;
            allNewItems.forEach((newItem) => {
              if (item.id == newItem.id) {
                used = true;
                res.push(item);
              }
            });
            if (!used) {
              // item.delete();// why is "copied" Connection not seable???
            }
          });

          itemsExt.forEach((item) => {
            var used = false;
            allNewItems.forEach((newItem) => {
              if (item.id == newItem.id) {
                used = true;
                res.push(item);
              }
            });
            if (!used) {
              // item.delete();
            }
          });

          return { newItems: res, newReferencedItems: allNewItems };
        }
      }
    } catch (ex) {
      logger.error(ex);
    }
  }

  async loadItemsToStateClipboardItems(): Promise<string | undefined> {
    try {
      const clipboardItems = await navigator.clipboard.read();
      if (!clipboardItems || clipboardItems.length == 0) {
        return;
      }

      const firstItem = clipboardItems[0];

      for (const type of firstItem.types) {
        const blob = await clipboardItems[0].getType(type);
        const zipStorageHandler = new ZipStorageHandler();
        await zipStorageHandler.setBlob(blob);
        const items = new BaseModelMap<BaseModel>(BaseModel, undefined, undefined);
        items.id = "clipboard"; // no itx

        const savedObjegistry = state.objectRegistry;
        state.objectRegistry = new ObjectRegistry();
        //
        // load all objects from clipboard to global project/state/objectRegistry-> because of this beckup and set becup later in state.objectregristry
        //
        await items.loadFromEstadaStorage(
          undefined,
          undefined,
          async (id: string | undefined, parentId: string | undefined): Promise<any> => {
            return await zipStorageHandler.loadData(id, parentId);
          },
        );

        state.objectRegistry = savedObjegistry;

        //state.clipboardItems = items;

        return items.asArray()[0]?.className;
      }
    } catch (ex) {
      logger.error(ex);
    }
    return undefined;
  }

  async pastePropertiesTemplate(device: Device) {
    try {
      const txt = state.clipboard.getData();
      if (!txt) {
        return;
      }
      const blob = await convertBase64toBlobAsync(txt);
      logger.log(`second ClipbordItem`, txt);
      if (blob) {
        const zipStorageHandler = new ZipStorageHandler();
        await zipStorageHandler.setBlob(blob);
        const objectRegistry = state.objectRegistry;
        state.objectRegistry = new ObjectRegistry();
        const items = new BaseModelMap<BaseModel>(BaseModel, undefined, undefined);
        items.id = "clipboard"; // no itx

        await items.loadFromEstadaStorage(
          undefined,
          undefined,
          async (id: string | undefined, parentId: string | undefined): Promise<any> => {
            return await zipStorageHandler.loadData(id, parentId);
          },
        );
        state.objectRegistry = objectRegistry;

        state.clipboardItems = items;
        const copyItemsArray = items.asArray();

        const copyItem: BaseModel | undefined = copyItemsArray[0];

        if (copyItem.className == device.className) {
          const copyDevice = copyItem as Device;
          //gleiche template id
          if (copyDevice.templateId == device.templateId) {
            device.knxInstanceData.pesistentParams = copyDevice.knxInstanceData.pesistentParams;
            device.knxInstanceData.pesistentModules = copyDevice.knxInstanceData.pesistentModules;
            // // set dirty??

            copyDevice.channels.forEach((channel) => {
              let toChannel = device.channels.getItemByTag(channel.tag);
              if (!toChannel) {
                toChannel = device.createNewRequestChannel();
              }
              toChannel.name = channel.name;
              toChannel.tag = channel.tag;
              toChannel.channelType = channel.channelType;
              toChannel.rockerConfig = channel.rockerConfig;
            });

            device.updateAllFromKnxMetadata().then(() => {
              //because of rocker config
              device.channels.forEach((channel) => {
                if (!channel.knxChannel && channel.getConnectedConsumer() == undefined) {
                  channel.delete();
                }
              });
            });

            device.knxInstanceData.raiseOnChange();

            showToast({
              text: state.translate("menubarActions.templateSettingsAdapted", "templateSettingsAdapted"),
            });
          } else {
            showToast({
              text: state.translate(
                "menubarActions.copiedDeviceDontHasTheSameTemplateAsThisDevice",
                "copiedDeviceDontHasTheSameTemplateAsThisDevice",
              ),
            });
          }
        } else {
          showToast({
            text: state.translate("menubarActions.copiedItemIsn`tFromTheSameType", "copiedItemIsn`tFromTheSameType"),
          });
        }
      } else {
        showToast({ text: state.translate("error", "error") });
      }
    } catch {
      logger.error("TabGeneral pasteProperty error");
    }
  }
}

function extendCopyItems(items: BaseModelMap<BaseModel>, itemsExt: BaseModelMap<BaseModel>, item: BaseModel) {
  const exendetItems = item.getExtendedCopyCollection(item);
  exendetItems?.forEach((item1) => {
    if (!items.items.has(item1.id) && !itemsExt.items.has(item1.id)) {
      itemsExt.addWithoutUpdate(item1);
      extendCopyItems(items, itemsExt, item1);
    }
  });
}
function convertBlobToBase64Async(blob: Blob): Promise<string | null> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      var res = reader.result as string;
      if (res) {
        // res = res.split("base64,")[1];
        resolve(res);
      } else {
        resolve(null);
      }
      // const dataUrlPrefix = `data:${blob.type};base64,`;
      // const base64WithDataUrlPrefix = reader.result;
      // const base64 = base64WithDataUrlPrefix.replace(dataUrlPrefix, '');
      // resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}
export async function convertBase64toBlobAsync(base64: string): Promise<Blob | null> {
  try {
    return (await fetch(base64)).blob();
  } catch {
    return null;
  }
}
